/**
 * Exercise Workspace
 * 
 * The main workspace for Quick Exercises - a cycle-hidden, cycle-backed UX.
 * Composes existing platform components into a simplified tabbed interface.
 * 
 * Per Build Contract:
 * - Reuses canonical components (ArrangeCriteriaPanel, ToolPicker, etc.)
 * - All state is DB-backed via existing stores
 * - No duplicate/parallel data flows
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Zap,
  Package,
  ListChecks,
  Wrench,
  FileText,
  ArrowLeft,
  Loader2,
} from 'lucide-react';
import { useDegapProcessStore } from '@/stores/prioritization/degapProcessStore';
import { useTrackStore } from '@/stores/prioritization/trackStore';
import { createTrackWithFeedback } from '@/lib/tracks/createTrackFlow';
import { useItemsStore } from '@/stores/prioritization/itemsStore';
import { useCycleParticipationStore } from '@/stores/prioritization/cycleParticipationStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useCycleComparisonSetStore } from '@/stores/prioritization/cycleComparisonSetStore';
import { useAttributeGovernanceStore } from '@/stores/prioritization/attributeGovernanceStore';
import { useValueProposalStore } from '@/stores/prioritization/valueProposalStore';
import { useItemPoolsStore } from '@/stores/prioritization/itemPoolsStore';
import { useAttributesStore } from '@/stores/prioritization/attributesStore';
import { useArrangeStore } from '@/stores/prioritization/arrangeStore';
import { useCycleProgress } from '@/stores/prioritization/cycleProgressStore';

// Extended snapshot type from arrangeStore (has additional fields from DB)
interface ArrangeSnapshot {
  id: string;
  toolName: string;
  createdAt: Date;
  rankedItems?: Array<{ id: string; title?: string; rank?: number; score?: number }>;
  [key: string]: unknown;
}

// Platform components (reused, not duplicated)
import { GatherReadinessSection } from '@/components/prioritization/gather';
import { ArrangeValueConvergencePanel } from '@/components/prioritization/arrange/ArrangeValueConvergencePanel';
import { ExerciseItemsPanel } from '@/components/prioritization/exercises/ExerciseItemsPanel';
import { ToolPicker } from '@/components/prioritization/ToolPicker';
import { ToolRunner } from '@/components/prioritization/ToolRunner';
import type { Item } from '@/types/prioritization/lifecycle';

type TabId = 'items' | 'criteria' | 'tools' | 'results';

interface Tab {
  id: TabId;
  label: string;
  icon: typeof Package;
}

const TABS: Tab[] = [
  { id: 'items', label: 'Items', icon: Package },
  { id: 'criteria', label: 'Criteria & Values', icon: ListChecks },
  { id: 'tools', label: 'Tools', icon: Wrench },
  { id: 'results', label: 'Results', icon: FileText },
];

export default function ExerciseWorkspace() {
  const { exerciseId } = useParams<{ exerciseId: string }>();
  const userId = useUserStore(state => state.userId);
  
  const [activeTab, setActiveTab] = useState<TabId>('items');
  const [isLoading, setIsLoading] = useState(true);
  const [defaultTrackId, setDefaultTrackId] = useState<string | null>(null);
  
  // Store selectors
  const cycles = useDegapProcessStore(state => state.cycles);
  const setActiveCycle = useDegapProcessStore(state => state.setActiveCycle);
  const fetchFromSupabase = useDegapProcessStore(state => state.fetchFromSupabase);

  const getDefaultTrack = useTrackStore(state => state.getDefaultTrack);
  const fetchTracks = useTrackStore(state => state.fetchAllTracks);
  
  const items = useItemsStore(state => state.items);
  const fetchCandidates = useItemsStore(state => state.fetchFromSupabase);
  
  const fetchCycleParticipation = useCycleParticipationStore(state => state.fetchFromSupabase);
  // Subscribe to participations state directly (not just the selector function)
  // This ensures re-render when participations change after hydration
  const participations = useCycleParticipationStore(state => state.participations);
  
  const fetchCycleComparisonSets = useCycleComparisonSetStore(state => state.fetchForCycle);
  const fetchAttributeGovernance = useAttributeGovernanceStore(state => state.fetchFromSupabase);
  const fetchValueProposals = useValueProposalStore(state => state.fetchFromSupabase);
  const acceptedValues = useValueProposalStore(state => state.acceptedValues);
  const fetchItemPools = useItemPoolsStore(state => state.fetchFromSupabase);
  const fetchAttributes = useAttributesStore(state => state.fetchFromSupabase);
  
  // Cast snapshots to extended type (arrangeStore has DB-derived fields)
  const snapshots = useArrangeStore(state => state.snapshots) as unknown as ArrangeSnapshot[];
  
  // Get cycle progress for components that need it
  const { progress: cycleProgress } = useCycleProgress(exerciseId);
  
  // Find the exercise
  const exercise = useMemo(() => {
    return cycles.find(c => c.id === exerciseId);
  }, [cycles, exerciseId]);
  
  // Get items for this exercise
  const exerciseItemIds = useMemo(() => {
    if (!exerciseId) return [];
    // Filter participations for this exercise (not excluded)
    return Object.values(participations)
      .filter(p => p.cycleId === exerciseId && p.cycleStatus !== 'excluded')
      .map(p => p.itemId);
  }, [exerciseId, participations]);
  
  const exerciseItems = useMemo(() => {
    const filtered = items.filter(c => exerciseItemIds.includes(c.id));
    // Map items to the format expected by harmonizer tools
    // Tools require 'id', 'label', and 'attributes'
    // Per Doc 09, attributes come from accepted_values via valueProposalStore
    return filtered.map(c => {
      // Build attributes from accepted values for this item
      const itemAcceptedValues = acceptedValues.filter(av => av.itemId === c.id);
      const attributes: Record<string, unknown> = {};
      
      for (const av of itemAcceptedValues) {
        // Normalize attribute keys to lowercase for tool compatibility
        // Tools like Impact-Effort Matrix expect exact keys 'impact', 'effort'
        const key = av.attributeKey.toLowerCase();
        attributes[key] = av.value;
      }
      
      return {
        id: c.id,
        label: c.title, // Map title to label for tool compatibility
        description: c.description,
        attributes,
      };
    }) as unknown as Item[];
  }, [items, exerciseItemIds, acceptedValues]);
  
  // Hydrate all necessary data on mount
  useEffect(() => {
    const hydrate = async () => {
      if (!exerciseId) return;
      
      setIsLoading(true);
      try {
        // Hydrate all stores needed for the exercise workspace
        await Promise.all([
          fetchFromSupabase(),
          fetchCandidates(),
          fetchCycleParticipation(),
          fetchCycleComparisonSets(exerciseId),
          fetchAttributeGovernance(),
          fetchValueProposals(),
          fetchItemPools(),
          fetchAttributes(),
          fetchTracks(),
        ]);
        
        // Set this as the active cycle
        setActiveCycle(exerciseId);

        // Ensure a default track exists — cycle_attribute_configs.track_id is NOT NULL,
        // so criteria/scale work requires a real track even for exercises.
        let track = getDefaultTrack(exerciseId);
        if (!track) {
          const cycle = useDegapProcessStore.getState().cycles.find(c => c.id === exerciseId);
          const result = await createTrackWithFeedback({
            cycleId: exerciseId,
            name: cycle?.name ?? 'Exercise',
            description: 'Default track for this quick exercise',
            scopeType: 'default',
            scopeLabel: 'Default',
          });
          if (!result.ok) {
            console.warn('[ExerciseWorkspace] Could not create default track:', result.error);
          }
          track = result.track;
        }
        if (track) setDefaultTrackId(track.id);
      } catch (error) {
        console.error('[ExerciseWorkspace] Hydration error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    hydrate();
  }, [
    exerciseId,
    fetchFromSupabase,
    fetchCandidates,
    fetchCycleParticipation,
    fetchCycleComparisonSets,
    fetchAttributeGovernance,
    fetchValueProposals,
    fetchItemPools,
    fetchAttributes,
    fetchTracks,
    setActiveCycle,
    getDefaultTrack,
  ]);
  
  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-[var(--text-ter)]" />
          <span className="text-[var(--text-ter)]">Loading exercise...</span>
        </div>
      </div>
    );
  }
  
  // Exercise not found
  if (!exercise) {
    return (
      <div className="card text-center py-12">
        <Package className="h-12 w-12 text-[var(--text-ter)] mx-auto mb-4" />
        <h3 className="text-lg font-medium text-[var(--text-pri)] mb-2">Exercise not found</h3>
        <p className="text-[var(--text-ter)] mb-4">
          The exercise you're looking for doesn't exist or has been deleted.
        </p>
        <Link
          to="/prioritization/exercises"
          className="inline-flex items-center gap-2 px-4 py-2 text-[var(--text-ter)] hover:bg-[var(--slate-dim)]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Exercises
        </Link>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/prioritization/exercises"
            className="p-2 hover:bg-[var(--hover-tint)] transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-[var(--text-ter)]" />
          </Link>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-[var(--text-pri)]">{exercise.name}</h1>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)]">
                <Zap className="h-3 w-3" />
                Quick Exercise
              </span>
            </div>
            {exercise.description && (
              <p className="text-[var(--text-sec)] mt-1">{exercise.description}</p>
            )}
          </div>
        </div>
        
        {/* Stats */}
        <div className="flex items-center gap-4 text-sm text-[var(--text-ter)]">
          <div className="flex items-center gap-1">
            <Package className="h-4 w-4" />
            <span>{exerciseItems.length} items</span>
          </div>
          <div className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            <span>{snapshots.length} snapshots</span>
          </div>
        </div>
      </div>
      
      {/* Tab Navigation */}
      <div className="border-b border-[var(--rule)]">
        <nav className="flex gap-4">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm
                  transition-colors
                  ${isActive
                    ? 'border-[var(--slate)] text-[var(--text-ter)]'
                    : 'border-transparent text-[var(--text-ter)] hover:text-[var(--text-sec)] hover:border-[var(--rule)]'
                  }
                `}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>
      
      {/* Tab Content */}
      <div className="min-h-[400px]">
        {/* Candidates Tab */}
        {activeTab === 'items' && (
          <ExerciseItemsPanel exerciseId={exerciseId!} />
        )}
        
        {/* Criteria & Values Tab */}
        {activeTab === 'criteria' && (
          <div className="space-y-6">
            {/* Readiness section handles criteria selection + scale definition */}
            {defaultTrackId ? (
              <GatherReadinessSection
                cycleId={exerciseId!}
                cycleName={exercise.name}
                trackId={defaultTrackId}
                userId={userId}
                cycleProgress={cycleProgress ?? null}
                onCriteriaLocked={() => { /* No-op in exercise mode */ }}
                onRefresh={() => { /* No-op in exercise mode */ }}
              />
            ) : (
              <div className="flex items-center gap-2 text-[var(--text-ter)] text-sm py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                Setting up criteria workspace…
              </div>
            )}
            
            {/* Value Convergence - uses platform component for value entry */}
            {/* Per Doc 07, this is the "Enter values" step before tool selection */}
            <ArrangeValueConvergencePanel cycleId={exerciseId!} />
          </div>
        )}
        
        {/* Tools Tab */}
        {activeTab === 'tools' && (
          <div className="space-y-6">
            {/* Tool Picker - uses platform component with phase gate bypass */}
            <div className="card">
              <h3 className="text-lg font-medium text-[var(--text-pri)] mb-4">Select Tool</h3>
              <ToolPicker cycleId={exerciseId} skipPhaseGate />
            </div>
            
            {/* Tool Runner - uses platform component with phase gate bypass */}
            <div className="card">
              <h3 className="text-lg font-medium text-[var(--text-pri)] mb-4">Run Tool</h3>
              <ToolRunner
                items={exerciseItems}
                cycleId={exerciseId}
                skipPhaseGate
              />
            </div>
          </div>
        )}
        
        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="space-y-6">
            {/* Snapshot History */}
            <div className="card">
              <h3 className="text-lg font-medium text-[var(--text-pri)] mb-4">Tool Results</h3>
              {snapshots.length > 0 ? (
                <div className="space-y-3">
                  {snapshots.map((snapshot) => (
                    <div
                      key={snapshot.id}
                      className="border border-[var(--rule)] p-4 hover:bg-[var(--surface)] transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium">{snapshot.toolName}</div>
                        <div className="text-xs text-[var(--text-ter)]">
                          {new Date(snapshot.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-sm text-[var(--text-sec)]">
                        {snapshot.rankedItems?.length || 0} items ranked
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
                  <p className="text-[var(--text-ter)]">No results yet.</p>
                  <p className="text-sm text-[var(--text-ter)] mt-1">
                    Run a prioritization tool to generate results.
                  </p>
                </div>
              )}
            </div>
            
            {/* Export Section */}
            {snapshots.length > 0 && (
              <div className="card">
                <h3 className="text-lg font-medium text-[var(--text-pri)] mb-4">Export</h3>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      // Export as CSV
                      const latestSnapshot = snapshots[0];
                      const items = latestSnapshot.rankedItems || [];
                      const csv = items
                        .map((item, idx) => 
                          `${idx + 1},"${item.title || item.id}",${item.score || ''}`
                        )
                        .join('\n');
                      const blob = new Blob([`Rank,Title,Score\n${csv}`], { type: 'text/csv' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${exercise.name}-results.csv`;
                      a.click();
                    }}
                    className="px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
                  >
                    Export CSV
                  </button>
                  <button
                    onClick={() => {
                      // Export as JSON
                      const latestSnapshot = snapshots[0];
                      const json = JSON.stringify(latestSnapshot, null, 2);
                      const blob = new Blob([json], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${exercise.name}-results.json`;
                      a.click();
                    }}
                    className="px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
                  >
                    Export JSON
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
